import React, { useRef, useState } from "react";

import GoogleMapReact from "google-map-react";
import Measure from "react-measure";
import { SchoolMarker } from "./SchoolMarker/web";
import getConfig from "next/config";
import { ImagenOptimizada } from "shared-components/Components/Image/web";

const { googleMapsApiKey } = getConfig().publicRuntimeConfig;

interface GoogleMapProps {
	defaultZoom: number;
	height: any;
	width: any;
	streetView: any;
	center: {
		latitude: any;
		longitude: any;
	};
	children: any;
	options: any;
	showSchools?: boolean;
}

export function GoogleMap({
	defaultZoom = 18,
	height = null,
	width = null,
	streetView = false,
	center,
	children = null,
	options = {},
	showSchools = false,
}: GoogleMapProps) {
	const MAP = useRef(null);
	const MAPS = useRef(null);
	const mapDOMElement = useRef(null);
	const [measure, setMeasure] = useState(null);
	const [schools, setSchools] = useState([]);
	const [displayMap, setDisplayMap] = useState(true);

	const processSVData = (data, status) => {
		if (status === "OK") {
			setDisplayMap(true);
		} else {
			setDisplayMap(false);
		}
	};

	const handleApiLoaded = (map, maps) => {
		MAP.current = map;
		MAPS.current = maps;

		if (streetView) {
			const sv = new maps.StreetViewService();
			const location = { lat: center.latitude, lng: center.longitude };

			sv.getPanorama({ location, radius: 50 }, processSVData);

			new maps.StreetViewPanorama(mapDOMElement.current.googleMapDom_ as HTMLElement, {
				position: location,
				pov: { heading: 165, pitch: 0 },
				zoom: 1,
			});
		}

		if (showSchools && maps.places.PlacesServiceStatus.OK == "OK") {
			const service = new maps.places.PlacesService(map);
			const bounds = map.getBounds();
			service.nearbySearch({ bounds: bounds, type: ["school"] }, (data, status) => {
				if (status == "OK") setSchools(data);
			});
		}
	};

	let googleMapReactOptions = {};
	if (options && options.marker_size) {
		googleMapReactOptions = {
			...googleMapReactOptions,
			distanceToMouse: (markerPos, mousePos, markerProps) => {
				const x = markerPos.x + options.marker_size / 2;
				const y = markerPos.y + options.marker_size / 2;
				const distanceKoef = 1; //markerProps.text !== 'A' ? 1.5 : 1;
				return (
					distanceKoef *
					Math.sqrt(
						(x - mousePos.x) * (x - mousePos.x) + (y - mousePos.y) * (y - mousePos.y)
					)
				);
			},
			hoverDistance: options.marker_size + 0,
		};
	}

	return (
		<Measure bounds onResize={m => m.entry && setMeasure(m)}>
			{({ measureRef }) => (
				<div ref={measureRef} style={{ width: "100%" }}>
					{measure && measure.entry && measure.entry.height && measure.entry.width && (
						<div
							style={{
								height: height ? height : measure.entry.height,
								width: width ? width : measure.entry.width,
								position: "relative",
							}}>
							{displayMap ? (
								<GoogleMapReact
									ref={mapDOMElement}
									bootstrapURLKeys={{
										key: googleMapsApiKey,
										libraries: ["places"],
									}}
									defaultCenter={{ lng: center.longitude, lat: center.latitude }}
									defaultZoom={defaultZoom}
									options={{
										clickableIcons: false,
										draggable: true,
										zoomControl: true,
										scrollwheel: true,
										disableDoubleClickZoom: false,
										...options,
									}}
									{...googleMapReactOptions}
									yesIWantToUseGoogleMapApiInternals
									onGoogleApiLoaded={x => handleApiLoaded(x.map, x.maps)}>
									{children}
									{schools.map((s, i) => {
										return (
											<SchoolMarker
												key={"school-marker-" + i}
												lat={s.geometry.location.lat()}
												lng={s.geometry.location.lng()}
												address={s.vicinity}
												name={s.name}
												photo={
													s.photos && s.photos.length > 0
														? s.photos[0].getUrl()
														: undefined
												}
												status={
													s.business_status == "OPERATIONAL"
														? "open"
														: "closed"
												}
												mapCenter={center}
											/>
										);
									})}
								</GoogleMapReact>
							) : (
								<ImagenOptimizada />
							)}
						</div>
					)}
				</div>
			)}
		</Measure>
	);
}
